import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Isvg from 'react-inlinesvg';

import Button from 'components/Button';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';

import { bem } from 'helpers/class-name';
import { isVenueDashboardSubtypeFoodAndBeverage } from 'helpers/ia-logic';
import { naiveDateStringToDateObject } from 'helpers/intl-fns';
import { selectActiveVenue } from 'selectors/general';

import { ICONS } from 'constants/icons';
import { SCHEDULE_DAYS } from '../CampaignsForm/CampaignsForm.constants';

import { CAMPAIGN_LEVEL, CAMPAIGN_TYPE } from '../Campaigns.helpers';
import parentMessages from '../Campaigns.messages';

import { campaignsTooltipsMessages, messages } from './CampaignsConfirmationModal.messages';

import './CampaignsConfirmationModal.scss';

const { block, element } = bem('campaigns-confirmation-modal');

function CampaignsConfirmationModal({
  isAppointmentTypeVenue,
  startDate,
  endDate,
  classes,
  discountPercentage,
  onModalConfirm,
  onClose,
  setEndDate,
  partnerCampaignType,
  timeSlots,
  isCreate = false,
}) {
  const intl = useIntl();
  const venue = useSelector(selectActiveVenue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverage(venue);

  const nameOverrides = isFoodAndBeverage ? parentMessages.fnbNameOverrides : parentMessages.nameOverrides;
  const disclaimerMessage = isCreate ? messages.disclaimerCreate : messages.disclaimerEdit;

  const adjustedStartDate = isCreate ? startDate : naiveDateStringToDateObject(startDate);
  const adjustedEndDate = isCreate ? endDate : naiveDateStringToDateObject(endDate);

  const scheduledDays = SCHEDULE_DAYS.filter(({ value }) => timeSlots?.[value]?.length > 0);

  function onConfirm() {
    setIsSubmitting(true);
    onModalConfirm();
  }

  const { icon, heading, all } = (() => {
    if (isFoodAndBeverage) {
      return { icon: ICONS.concierge, heading: messages.items, all: messages.allItems };
    }

    if (isAppointmentTypeVenue) {
      return { icon: ICONS.concierge, heading: messages.services, all: messages.allServices };
    }

    return { icon: ICONS.dumbbell, heading: messages.classes, all: messages.allClasses };
  })();

  return (
    <div className={block()}>
      <Message className={element('heading')} as="h2" message={messages.heading} />

      <section className={element('info-section')}>
        <article className={element('info-row')}>
          <Isvg className={element('info-row-icon')} src={ICONS.arrowChartUp} />
          <Message className={element('info-row-heading')} as="h3" message={messages.campaignType} />

          <span className={element('info-row-text')}>
            <Message message={nameOverrides[partnerCampaignType.name]} />

            <TooltipToggle name="campaign-type-tooltip" className={element('info-row-tooltip')}>
              <Isvg alt={intl.formatMessage(messages.moreInformation)} src={ICONS.info} />
            </TooltipToggle>
            <TooltipContent name="campaign-type-tooltip" hoverable place="right">
              <Message
                className={element('info-row-tooltip-content')}
                message={campaignsTooltipsMessages[partnerCampaignType.name]}
              />
            </TooltipContent>
          </span>
        </article>

        <div className={element('info-divider')} />

        <article className={element('info-row')}>
          <Isvg className={element('info-row-icon')} src={icon} />
          <Message className={element('info-row-heading')} as="h3" message={heading} />

          <When predicate={partnerCampaignType.level === CAMPAIGN_LEVEL.VENUE}>
            <Message className={element('info-row-text')} message={all} />
          </When>

          <When predicate={partnerCampaignType.level !== CAMPAIGN_LEVEL.VENUE}>
            <section className={element('info-row-collection')}>
              {classes.map(({ name: className, classId }) => (
                <div key={classId} className={element('pill')}>
                  {className}
                </div>
              ))}
            </section>
          </When>
        </article>

        <div className={element('info-divider')} />

        <article className={element('info-row')}>
          <Isvg src={ICONS.tagHollow} />
          <Message className={element('info-row-heading')} as="h3" message={messages.discountAmount} />
          <Message
            className={element('info-row-text')}
            message={messages.discountPercentage}
            values={{ value: discountPercentage }}
          />
        </article>

        <div className={element('info-divider')} />

        <article className={element('info-row')}>
          <Isvg src={ICONS.calendarDark} />
          <Message className={element('info-row-heading')} as="h3" message={messages.startDate} />
          <Message className={element('info-row-text')} value={new Date(adjustedStartDate)} format="medium" />
        </article>

        <When predicate={setEndDate && !!endDate}>
          <div className={element('info-divider')} />

          <article className={element('info-row')}>
            <Isvg src={ICONS.calendarDark} />
            <Message className={element('info-row-heading')} as="h3" message={messages.endDate} />
            <Message className={element('info-row-text')} value={new Date(adjustedEndDate)} format="medium" />
          </article>
        </When>

        <When predicate={scheduledDays.length > 0}>
          <div className={element('info-divider')} />

          <article className={element('info-row')}>
            <Isvg src={ICONS.calendar} />
            <Message className={element('info-row-heading')} as="h3" message={messages.schedule} />
            <div className={element('info-row-text', { schedule: true })}>
              {scheduledDays.map(({ value, messageFull }) => (
                <p key={value} className="m-0">
                  <Message as="strong" message={messageFull} />
                  {': '}
                  {timeSlots[value].map(({ startTime, endTime }) => `${startTime}–${endTime}`).join(', ')}
                </p>
              ))}
            </div>
          </article>
        </When>
      </section>

      <section className={element('disclaimer')}>
        <Isvg className={element('disclaimer-icon')} src={ICONS.infoGreyLight} />
        <Message className={element('disclaimer-text')} message={disclaimerMessage} />
      </section>

      <section className={element('buttons')}>
        <Message
          className={element('button')}
          as={Button}
          styled="tertiary"
          message={messages.cancel}
          onClick={onClose}
        />

        <When predicate={isCreate}>
          <Message
            className={element('button')}
            as={Button}
            message={messages.launchCampaign}
            onClick={onConfirm}
            waiting={isSubmitting}
          />
        </When>

        <When predicate={!isCreate}>
          <Message
            className={element('button')}
            as={Button}
            message={messages.confirm}
            onClick={onConfirm}
            waiting={isSubmitting}
          />
        </When>
      </section>
    </div>
  );
}

CampaignsConfirmationModal.propTypes = {
  isAppointmentTypeVenue: PropTypes.bool,
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  discountPercentage: PropTypes.number,
  onModalConfirm: PropTypes.func,
  onClose: PropTypes.func,
  setEndDate: PropTypes.bool,
  partnerCampaignType: PropTypes.shape({
    name: PropTypes.oneOf(Object.keys(CAMPAIGN_TYPE)),
    level: PropTypes.oneOf(Object.keys(CAMPAIGN_LEVEL)),
  }),
  timeSlots: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.shape({ startTime: PropTypes.string, endTime: PropTypes.string }))
  ),
  isCreate: PropTypes.bool,
};

export default CampaignsConfirmationModal;
